// Owner: platform-agent. The state of preview mode (preview-state.ts has the way in and out, PreviewBar the
// bar): tiny and without messages (the one contract module it reads holds schemas only), so the shell and
// the rail can read it in the main bundle.
import { useMemo, useSyncExternalStore } from 'react';
import { z } from 'zod';
import { AiPreviewDTO } from '@dataroom/contracts/ai/change-sets';
import { onPreviewEnded, setPreviewHeader } from '../../lib/preview-header';
import { queryClient } from '../../lib/queries';
import { readStored } from '../../lib/stored';

const PreviewSession = AiPreviewDTO.extend({
  /** The plan's title ("Set up project management"). */
  title: z.string(),
  /** The approval the preview belongs to: Apply and Discard answer it. */
  interruptId: z.string(),
  /** Objects the plan creates (the sidebar marks them). */
  objects: z.array(z.string()),
  /** Where the member was before the preview (Discard goes back there). */
  returnTo: z.string(),
});
export type PreviewSession = z.infer<typeof PreviewSession>;

/** What the Copilot panel does for the bar while it is open (it owns the run that waits). */
export type PreviewControls = { apply: () => void; discard: () => void };

type PendingAction = { action: 'apply' | 'discard'; interruptId: string };

type State = {
  session: PreviewSession | null;
  /** Opening: the server replays the plan once before the app switches to it. */
  opening: string | null;
  /** Asked from the bar while the panel was closed: the panel does it when it opens. */
  pending: PendingAction | null;
};

/** The preview survives a reload of the tab (the server keeps it for 30 minutes). */
const STORAGE_KEY = 'dr.preview';
const restored = (): PreviewSession | null => readStored(STORAGE_KEY, PreviewSession, 'session');

let state: State = { session: restored(), opening: null, pending: null };
setPreviewHeader(state.session?.id ?? null);
const listeners = new Set<() => void>();
const controls = new Map<string, PreviewControls>();

export function setPreviewStore(next: Partial<State>): void {
  const before = state.session;
  state = { ...state, ...next };
  if (state.session !== before)
    try {
      if (state.session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.session));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Private mode: the preview just does not survive a reload.
    }
  for (const l of [...listeners]) l();
}

// The server ended the preview (expired, restarted): the app shows what is really there again.
onPreviewEnded(() => {
  setPreviewHeader(null);
  setPreviewStore({ session: null });
  void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'ai' });
});
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function usePreviewState(): State {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}
export const previewState = (): State => state;

/** The panel registers how to answer the approval while it is open. */
export function registerPreviewControls(interruptId: string, c: PreviewControls): () => void {
  controls.set(interruptId, c);
  return () => {
    if (controls.get(interruptId) === c) controls.delete(interruptId);
  };
}
export const previewControls = (interruptId: string): PreviewControls | undefined =>
  controls.get(interruptId);

/** Asked from the bar while the panel is closed: remembered for the panel to do once it is open. */
export function requestPreviewAction(action: 'apply' | 'discard', interruptId: string): void {
  setPreviewStore({ pending: { action, interruptId } });
}
/** The action asked for this approval, once (the panel calls it when the approval is on screen). */
export function takePendingAction(interruptId: string): 'apply' | 'discard' | null {
  const pending = state.pending;
  if (pending?.interruptId !== interruptId) return null;
  setPreviewStore({ pending: null });
  return pending.action;
}

const NONE: ReadonlySet<string> = new Set();

/** Fields the shown preview adds to an object that exists already (its lists highlight these columns). */
export function usePreviewNewFields(objectKey: string): ReadonlySet<string> {
  const fields = usePreviewState().session?.fields;
  return useMemo(
    () => (fields?.length ? new Set(fields.filter((f) => f.object === objectKey).map((f) => f.key)) : NONE),
    [fields, objectKey],
  );
}

/** Objects that exist already and get new fields from the shown preview (the rail marks them). */
export function previewExtended(session: PreviewSession | null): string[] {
  return [...new Set((session?.fields ?? []).map((f) => f.object))].filter(
    (o) => !session?.objects.includes(o),
  );
}
